import React, { useState, useRef } from 'react';
import { HelpCircle } from 'lucide-react';

interface HelpTipProps {
  text: React.ReactNode;
  /** Tailwind width class for the tooltip box — defaults to w-64 */
  width?: string;
  /** 'above' (default) or 'below' */
  position?: 'above' | 'below';
}

/**
 * Inline help icon that shows a tooltip on hover.
 * Usage: <HelpTip text="Explanation of the field" />
 */
export const HelpTip: React.FC<HelpTipProps> = ({
  text,
  width = 'w-64',
  position = 'above',
}) => {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setVisible(true);
  };

  const hide = () => {
    timerRef.current = setTimeout(() => setVisible(false), 80);
  };

  const above = position === 'above';

  return (
    <span className="relative inline-flex items-center">
      <HelpCircle
        className="ml-1 h-3.5 w-3.5 cursor-help text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
        onMouseEnter={show}
        onMouseLeave={hide}
      />
      {visible && (
        <span
          className={`pointer-events-none absolute left-1/2 z-50 -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-xs leading-relaxed text-white shadow-xl ${width} dark:bg-gray-700 ${
            above ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
          onMouseEnter={show}
          onMouseLeave={hide}
        >
          {text}
          <span
            className={`absolute left-1/2 -translate-x-1/2 border-4 border-transparent ${
              above
                ? 'top-full border-t-gray-900 dark:border-t-gray-700'
                : 'bottom-full border-b-gray-900 dark:border-b-gray-700'
            }`}
          />
        </span>
      )}
    </span>
  );
};
